import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { DrawingLayer } from "./DrawingLayer";
import { AnnotationLayer } from "./AnnotationLayer";
import {
  formatPriceToSevenDigits,
  useRealtimeAreaChart,
  LIVE_PRICE_UP_COLOR,
  LIVE_PRICE_DOWN_COLOR,
} from "../hooks/useRealtimeAreaChart";
import {
  buildCandlesFromTicks,
  timeframeToSeconds,
} from "../lib/candleAggregation";
import { BinanceTradeFeed } from "../lib/binanceTradeFeed";
import { fetchHistoricalTickData } from "../lib/binanceHistoricalData";
import { generateInitialHistory } from "../lib/generateInitialHistory";
import { cn } from "../lib/shadcn-utils";
import {
  INITIAL_TICK_HISTORY_POINTS,
  LINE_VISIBLE_HISTORY_POINTS,
  MAX_STORED_TICK_HISTORY_POINTS,
  CANDLE_SOURCE_HISTORY_POINTS,
  FEED_FRAME_MIN_MS,
  MIN_DISPLAY_PRICE,
  INTERVAL_CHANGE_LOADER_MS,
  IDLE_MICRO_MOVE_AFTER_MS,
  IDLE_MICRO_MOVE_INTERVAL_MS,
  DEFAULT_OVERLAY_GREEN_ALPHA,
  DEFAULT_OVERLAY_RED_ALPHA,
  DEFAULT_CHART_TYPE,
  LEFT_EDGE_CLAMP_X,
} from "./chart/chartConstants";
import {
  resolveChartStartPrice,
  clamp,
  resolveFeedAnimationDurationMs,
  resolveSmoothJumpDurationMs,
  easeInOutCubic,
  interpolateTickDisplayPrice,
  resolveIdleMicroPrice,
  isMeaningfulPriceChange,
  applyTickCandleData,
  resolveTimeMarkerCoordinate,
  expand1sToTickHistory,
  resolveHistoricalFetchConfig,
  areTradingChartPropsEqual,
} from "./chart/chartUtils";
import { OrderTimeMarkersLayer } from "./chart/OrderTimeMarkersLayer";
import { ActiveTradesLayer } from "./chart/ActiveTradesLayer";
import { LiveMarkerLayer } from "./chart/LiveMarkerLayer";

const TradingChartComponent = forwardRef(function TradingChartComponent(
  {
    symbol,
    precision,
    tickSize,
    marketSymbol,
    startingPrice,
    timeframe,
    chartType = DEFAULT_CHART_TYPE,
    drawingEnabled,
    drawingTool,
    drawingsResetKey,
    disableInteractions = false,
    hideTimeScale = false,
    disableZoomToLatest = false,
    feedMode = "binance",
    randomWalkStep = 3,
    randomWalkIntervalMs = 500,
    fixedZoomPercent = null,
    keepLatestCentered = true,
    centeredVisibleBars = null,
    tickVisibleHistoryPoints = LINE_VISIBLE_HISTORY_POINTS,
    accurateLivePrice = false,
    forwardSpaceBars = 2,
    forceTickEveryMs = 0,
    smoothContinuousMotion = false,
    smoothShiftDurationMs = null,
    directionalOverlay = false,
    chartBackgroundColor = null,
    chartFontSize = null,
    priceScaleStep = null,
    priceScaleLevels = null,
    enableSyntheticFallback = true,
    pinPriceScale = true,
    showLiveMarker = true,
    showLivePriceLine = true,
    showOrderTimeMarkers = false,
    referencePriceLines = [],
    chartProfile = null,
    cardClassName = "",
    stageClassName = "",
    canvasClassName = "",
    overlayClassName = "",
    onOrderSettled,
    activeOrders = [],
  },
  ref,
) {
  const normalizedStartingPrice = resolveChartStartPrice(startingPrice);
  const timeframeRef = useRef(timeframe);
  timeframeRef.current = timeframe;
  const chartContainerRef = useRef(null);
  const directionalOverlayRef = useRef(null);
  const livePriceLabelRef = useRef(null);
  const symbolBadgeRef = useRef(null);
  const liveLevelRef = useRef(null);
  const liveDotRef = useRef(null);
  const livePillRef = useRef(null);
  const liveMarkerXPercentRef = useRef(null);
  const liveMarkerPositionRef = useRef(null);
  const expiryMarkerLayerRef = useRef(null);
  const orderTimeMarkersRef = useRef([]);
  const removedOrderIdsRef = useRef(new Set());
  const activeTradeLayerRef = useRef(null);
  const getTimeCoordinateRef = useRef(null);
  const initialHistoryRef = useRef(
    generateInitialHistory(
      normalizedStartingPrice,
      INITIAL_TICK_HISTORY_POINTS,
    ),
  );
  const globalTickHistoryRef = useRef(null);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [isHistoricalReady, setIsHistoricalReady] = useState(
    feedMode === "randomWalk",
  );
  const [gameAnnotations, setGameAnnotations] = useState([]);
  const [orderTimeMarkers, setOrderTimeMarkers] = useState([]);
  const lastOrderMarkerPositionRef = useRef(null);
  // Keep a ref so onLiveMarkerChange (a stable callback) sees the latest markers.
  orderTimeMarkersRef.current = orderTimeMarkers;
  const tradeResultOverlayRef = useRef(null);
  const tradeResultTimerRef = useRef(null);
  const hideChartLoader = false;



  useEffect(() => {
    const handler = (ev) => {
      try {
        const d = ev?.detail;
        if (!d) return;
        const eventMarket = String(d.marketSymbol || "").trim();
        if (!eventMarket) return;
        if (
          String(marketSymbol || "").toUpperCase() !== eventMarket.toUpperCase()
        )
          return;
        const oid = String(d.orderId || "").trim();
        if (!oid) return;
        // Skip if this order was explicitly removed (early-closed)
        if (removedOrderIdsRef.current.has(oid)) return;
        const rawMult = Number(d.payoutMultiplier || 1.82);
        const normalizedMult =
          rawMult > 10 ? 1 + rawMult / 100 : rawMult > 0 ? rawMult : 1.82;
        const entryPrice = Number(d.entryPrice) || lastRenderedPriceRef.current;
        const amount = Number(d.amount) || 0;
        const entryTime = Number(d.entryTime);
        const expiryTime = Number(d.expiryTime);

        const tradeMultiplier = Number(d.tradeMultiplier || d.jackpot_multiplier || d.appliedMultiplier || 1);
        setOrderTimeMarkers((prev) => {
          const existingIdx = prev.findIndex((m) => String(m.id || "") === oid);
          const newMarker = {
            id: oid,
            side: d.side,
            amount: amount || (existingIdx >= 0 ? prev[existingIdx].amount : 0),
            tradeMultiplier: tradeMultiplier > 1 ? tradeMultiplier : (existingIdx >= 0 ? prev[existingIdx].tradeMultiplier || 1 : 1),
            entryPrice:
              entryPrice ||
              (existingIdx >= 0
                ? prev[existingIdx].entryPrice
                : lastRenderedPriceRef.current),
            payoutMultiplier: normalizedMult,
            entryTime:
              Number.isFinite(entryTime) && entryTime > 0
                ? entryTime
                : existingIdx >= 0
                  ? prev[existingIdx].entryTime
                  : Date.now() / 1000,
            expiryTime:
              Number.isFinite(expiryTime) && expiryTime > 0
                ? expiryTime
                : existingIdx >= 0
                  ? prev[existingIdx].expiryTime
                  : Date.now() / 1000 + 30,
            expiresAt:
              (Number.isFinite(expiryTime) && expiryTime > 0
                ? expiryTime
                : Date.now() / 1000 + 30) + 0.35,
          };
          if (existingIdx >= 0) {
            const next = [...prev];
            next[existingIdx] = { ...next[existingIdx], ...newMarker };
            return next;
          }
          return [...prev, newMarker].slice(-16);
        });
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener("globalOrderMarker", handler);
    return () => window.removeEventListener("globalOrderMarker", handler);
  }, [marketSymbol]);

  const [viewportVersion, setViewportVersion] = useState(0);
  const [feedStartPriceSeed, setFeedStartPriceSeed] = useState(
    normalizedStartingPrice,
  );
  const viewportFrameRef = useRef(null);
  const historicalRequestIdRef = useRef(0);
  const historicalReadyRef = useRef(feedMode === "randomWalk");
  const lastFeedFrameAtRef = useRef(0);
  const queuedFeedTickRef = useRef(null);
  const feedRafRef = useRef(null);
  const timeframeLoaderTimerRef = useRef(null);
  const normalizedVisibleHistoryPoints = Number.isFinite(
    tickVisibleHistoryPoints,
  )
    ? Math.max(120, Math.round(tickVisibleHistoryPoints))
    : LINE_VISIBLE_HISTORY_POINTS;
  const lineData = useMemo(
    () => initialHistoryRef.current.slice(-normalizedVisibleHistoryPoints),
    [timeframe, historyVersion, normalizedVisibleHistoryPoints],
  );
  const candleData = useMemo(() => {
    if (timeframe === "Tick") {
      return [];
    }
    const candleSource = initialHistoryRef.current.slice(
      -CANDLE_SOURCE_HISTORY_POINTS,
    );
    return buildCandlesFromTicks(candleSource, timeframeToSeconds(timeframe));
  }, [timeframe, historyVersion]);
  const normalizedChartType =
    chartType === "area" ? "area" : DEFAULT_CHART_TYPE;
  const lastRenderedPriceRef = useRef(normalizedStartingPrice);
  const serverTimeOffsetRef = useRef(0);
  const getChartTime = useCallback(() => {
    return Date.now() / 1000 + serverTimeOffsetRef.current;
  }, []);
  const latestTickTimeRef = useRef(
    lineData[lineData.length - 1]?.time ?? Math.round((Date.now() / 1000) * 10) / 10,
  );
  const rafIdRef = useRef(null);
  const lastMeaningfulPriceChangeAtRef = useRef(Date.now());
  const idleMicroTimerRef = useRef(null);
  const idleMicroMotionRef = useRef({
    phase: 0,
    velocity: 0,
  });
  const pendingOrdersRef = useRef([]);
  const animationStateRef = useRef(null);
  const tickMotionRef = useRef({
    noise: 0,
    velocity: 0,
    spike: 0,
    phase: 0,
    displayPrice: null,
    spikeCooldownFrames: 0,
  });

  const simulationRef = useRef({
    active: false,
    targetPrice: null,
    velocity: 0,
    tradeId: null,
  });

  const overlayStateRef = useRef({
    green: DEFAULT_OVERLAY_GREEN_ALPHA,
    red: DEFAULT_OVERLAY_RED_ALPHA,
    baseGreen: DEFAULT_OVERLAY_GREEN_ALPHA,
    baseRed: DEFAULT_OVERLAY_RED_ALPHA,
    lastPrice: normalizedStartingPrice,
  });

  const resolveOverlayBaseAlpha = useCallback(() => {
    const overlayElement = directionalOverlayRef.current;
    if (!overlayElement) {
      return {
        green: DEFAULT_OVERLAY_GREEN_ALPHA,
        red: DEFAULT_OVERLAY_RED_ALPHA,
      };
    }
    const styles = window.getComputedStyle(overlayElement);
    const cssGreen = Number.parseFloat(
      styles.getPropertyValue("--overlay-green-alpha"),
    );
    const cssRed = Number.parseFloat(
      styles.getPropertyValue("--overlay-red-alpha"),
    );
    return {
      green: Number.isFinite(cssGreen)
        ? clamp(cssGreen, 0.08, 0.9)
        : DEFAULT_OVERLAY_GREEN_ALPHA,
      red: Number.isFinite(cssRed)
        ? clamp(cssRed, 0.08, 0.9)
        : DEFAULT_OVERLAY_RED_ALPHA,
    };
  }, []);

  useEffect(() => {
    if (feedMode === "randomWalk") {
      setFeedStartPriceSeed(normalizedStartingPrice);
    }
  }, [feedMode, marketSymbol, normalizedStartingPrice]);
  const historySeedKey =
    feedMode === "randomWalk"
      ? `${marketSymbol}:${normalizedStartingPrice}`
      : marketSymbol;
  const updateDirectionalOverlay = useCallback(
    (price, direction) => {
      const overlayElement = directionalOverlayRef.current;
      if (!overlayElement || !directionalOverlay || !Number.isFinite(price)) {
        return;
      }
      overlayElement.dataset.direction = direction;
      const state = overlayStateRef.current;
      const reference = Math.max(Math.abs(state.lastPrice), 1);
      const moveRatio = Math.abs(price - state.lastPrice) / reference;
      const impulse = clamp(moveRatio * 2000, 0, 0.48);
      const baseGreen = state.baseGreen ?? DEFAULT_OVERLAY_GREEN_ALPHA;
      const baseRed = state.baseRed ?? DEFAULT_OVERLAY_RED_ALPHA;
      const inactiveGreen = clamp(baseGreen * 0.36 + impulse * 0.08, 0.1, 0.34);
      const inactiveRed = clamp(baseRed * 0.36 + impulse * 0.08, 0.1, 0.34);
      if (direction === "up") {
        state.green = clamp(baseGreen + 0.2 + impulse, baseGreen, 0.92);
        state.red = inactiveRed;
      } else {
        state.red = clamp(baseRed + 0.2 + impulse, baseRed, 0.92);
        state.green = inactiveGreen;
      }
      state.lastPrice = price;
      overlayElement.style.setProperty(
        "--overlay-green-alpha",
        state.green.toFixed(3),
      );
      overlayElement.style.setProperty(
        "--overlay-red-alpha",
        state.red.toFixed(3),
      );
    },
    [directionalOverlay],
  );

  const updateLiveBadge = useCallback(
    (price, direction) => {
      const resolvedDirection = directionalOverlay
        ? price >= normalizedStartingPrice
          ? "up"
          : "down"
        : direction;
      if (livePriceLabelRef.current) {
        livePriceLabelRef.current.textContent = formatPriceToSevenDigits(price);
      }
      if (symbolBadgeRef.current) {
        symbolBadgeRef.current.dataset.direction = resolvedDirection;
      }
      if (liveLevelRef.current) {
        liveLevelRef.current.dataset.direction = resolvedDirection;
      }
      if (liveDotRef.current) {
        liveDotRef.current.dataset.direction = resolvedDirection;
      }
      if (livePillRef.current) {
        livePillRef.current.dataset.direction = resolvedDirection;
      }
      updateDirectionalOverlay(price, resolvedDirection);
    },
    [directionalOverlay, normalizedStartingPrice, updateDirectionalOverlay],
  );

  const onChartPriceChange = useCallback((price) => {
    if (!livePriceLabelRef.current) {
      return;
    }
    lastRenderedPriceRef.current = price;
    livePriceLabelRef.current.textContent = formatPriceToSevenDigits(price);
  }, []);

  const onLiveMarkerChange = useCallback(
    (position) => {
      if (!chartContainerRef.current) {
        return;
      }
      if (!position) {
        if (liveLevelRef.current) {
          liveLevelRef.current.style.opacity = "0";
        }
        if (liveDotRef.current) {
          liveDotRef.current.style.opacity = "0";
          delete liveDotRef.current.dataset.netState;
        }
        if (livePillRef.current) {
          livePillRef.current.style.opacity = "0";
        }
        liveMarkerXPercentRef.current = null;
        liveMarkerPositionRef.current = null;
        return;
      }
      const isLineMarker =
        normalizedChartType === "area" || timeframe === "Tick";
      const containerRect = chartContainerRef.current.getBoundingClientRect();
      const stageRect =
        chartContainerRef.current.parentElement?.getBoundingClientRect() ??
        containerRect;
      const width = chartContainerRef.current.clientWidth;
      const height = chartContainerRef.current.clientHeight;
      const stageWidth = Math.max(1, stageRect.width);
      const offsetX = containerRect.left - stageRect.left;
      const offsetY = containerRect.top - stageRect.top;
      const x =
        !isLineMarker && keepLatestCentered && width > 0
          ? width / 2
          : clamp(position.x, 0, width);
      const y = clamp(position.y, 0, height);
      const stageX = clamp(x + offsetX, 0, stageWidth);
      const stageY = clamp(y + offsetY, 0, Math.max(1, stageRect.height));
      liveMarkerXPercentRef.current =
        stageWidth > 0 ? (stageX / stageWidth) * 100 : null;
      liveMarkerPositionRef.current = { x: stageX, y: stageY };
      if (liveLevelRef.current) {
        liveLevelRef.current.style.opacity = "1";
        liveLevelRef.current.style.top = `${stageY}px`;
      }
      if (liveDotRef.current) {
        liveDotRef.current.style.opacity = "1";
        liveDotRef.current.style.left = `${stageX}px`;
        liveDotRef.current.style.top = `${stageY}px`;
      }
      if (livePillRef.current) {
        livePillRef.current.style.opacity = "1";
        livePillRef.current.style.top = `${stageY}px`;
        livePillRef.current.textContent = formatPriceToSevenDigits(position.price);
      }

      // ── Active Trade Indicators (Live Mode line graph only) ──
      const isLiveMode = timeframe === "Tick" && normalizedChartType === "area";
      const activeTradeLayer = activeTradeLayerRef.current;
      if (activeTradeLayer) {
        const now = Date.now() / 1000 + serverTimeOffsetRef.current;
        const markers = orderTimeMarkersRef.current;
        if (!isLiveMode) {
            activeTradeLayer.style.display = "none";
            if (liveDotRef.current) {
              delete liveDotRef.current.dataset.netState;
            }
          } else {
            activeTradeLayer.style.display = "";
            const clientWidth = chartContainerRef.current?.clientWidth ?? 800;
            const clientHeight = chartContainerRef.current?.clientHeight ?? 400;
            const liveTrades = activeTradeLayer.querySelectorAll(".live-trade-marker");
            liveTrades.forEach((el) => {
              const eid = el.dataset.markerId;
              const marker = markers.find((m) => String(m.id) === eid);
              if (!marker || now >= Number(marker.expiryTime) + 0.35) {
                el.style.opacity = "0";
                return;
              }

              // Get coordinates
              const effectiveEntryPrice = Number(marker.entryPrice) || lastRenderedPriceRef.current;
              const effectiveEntryTime = Number(marker.entryTime) || (now - 1);
              let coords = getCoordinates(effectiveEntryPrice, effectiveEntryTime);

              // If entry time has scrolled far off-screen left, coords might be null.
              // We derive the current vertical price level coordinate using current time:
              if (!coords || !Number.isFinite(coords.y)) {
                const priceOnlyCoords = getCoordinates(effectiveEntryPrice, now);
                if (priceOnlyCoords && Number.isFinite(priceOnlyCoords.y)) {
                  coords = { x: -999, y: priceOnlyCoords.y };
                }
              }

              if (!coords || !Number.isFinite(coords.y)) {
                el.style.opacity = "0";
                return;
              }

              // Vertical off-screen check (price out of visible vertical chart range)
              if (coords.y < -30 || coords.y > clientHeight + 30) {
                el.style.opacity = "0";
                return;
              }

              // Horizontal off-screen right check (future entry time)
              if (Number.isFinite(coords.x) && coords.x > clientWidth + 30) {
                el.style.opacity = "0";
                return;
              }

              // Determine whether to clamp to left edge
              const isOffScreenLeft = !Number.isFinite(coords.x) || coords.x <= LEFT_EDGE_CLAMP_X;
              let displayX = coords.x;
              let displayY = coords.y;

              if (isOffScreenLeft) {
                // Recompute exact Y at current viewport scale
                const priceOnlyCoords = getCoordinates(effectiveEntryPrice, now);
                if (priceOnlyCoords && Number.isFinite(priceOnlyCoords.y)) {
                  displayY = priceOnlyCoords.y;
                }
                displayX = LEFT_EDGE_CLAMP_X;
                el.dataset.clamped = "true";
              } else {
                el.dataset.clamped = "false";
              }

              // Calculate dynamic profit based on live price
              const isUp = marker.side === "up";
              const currentPrice = position.price;
              const won = isUp ? currentPrice > effectiveEntryPrice : currentPrice < effectiveEntryPrice;
              const isDraw = currentPrice === effectiveEntryPrice;

              const rawMult = Number(marker.payoutMultiplier || 1.82);
              const mult = rawMult > 10 ? 1 + rawMult / 100 : (rawMult > 0 ? rawMult : 1.82);
              const tradeMult = Number(marker.tradeMultiplier || marker.jackpot_multiplier || marker.appliedMultiplier || 1);
              const multiplier = tradeMult > 1 ? tradeMult : 1;
              let profitRaw = 0;
              const amount = Number(marker.amount) || 0;
              if (won) {
                profitRaw = amount * (mult - 1) * multiplier;
              } else if (!isDraw) {
                profitRaw = -amount;
              }

              const formatProfit = (val) => {
                const abs = Math.abs(val);
                if (abs === 0) return "0.00";
                if (abs % 1 === 0) return abs.toFixed(0);
                const s4 = abs.toFixed(4);
                const s3 = abs.toFixed(3);
                const s2 = abs.toFixed(2);
                if (Number(s4) !== Number(s3)) return abs.toFixed(4);
                if (Number(s3) !== Number(s2)) return abs.toFixed(3);
                return s2;
              };
              const profitStr = won ? `+$${formatProfit(profitRaw)}` : (isDraw ? "$0" : `-$${formatProfit(profitRaw)}`);
              const bubbleClass = won ? "is-won" : (isDraw ? "is-draw" : "is-lost");

              // Apply to DOM
              el.style.left = `${displayX}px`;
              el.style.top = `${displayY}px`;
              el.style.opacity = "1";

              const bubble = el.querySelector(".live-trade-bubble");
              if (bubble) {
                bubble.textContent = profitStr;
                bubble.className = `live-trade-bubble ${bubbleClass}`;
              }
            });

            // Compute net winning / losing state for the live dot
            const activeMarkers = markers.filter((m) => now < Number(m.expiryTime) + 0.35);
            if (activeMarkers.length > 0 && liveDotRef.current) {
              const currentPrice = position.price;
              let allWin = true;
              let allLoss = true;
              for (const m of activeMarkers) {
                const ep = Number(m.entryPrice) || lastRenderedPriceRef.current;
                const isUp = m.side === "up";
                const won = isUp ? currentPrice > ep : currentPrice < ep;
                const draw = currentPrice === ep;
                if (!won || draw) allWin = false;
                if (won || draw) allLoss = false;
              }
              if (allWin) {
                liveDotRef.current.dataset.netState = "winning";
              } else if (allLoss) {
                liveDotRef.current.dataset.netState = "losing";
              } else {
                liveDotRef.current.dataset.netState = "mixed";
              }
            } else if (liveDotRef.current) {
              delete liveDotRef.current.dataset.netState;
            }

            // Prune expired trade markers so active trade lines automatically clear
            const hasStaleMarkers = markers.some((m) => m.expiryTime && now >= Number(m.expiryTime) + 0.5);
            if (hasStaleMarkers) {
              setOrderTimeMarkers((prev) => prev.filter((m) => !m.expiryTime || now < Number(m.expiryTime) + 0.5));
            }
          }
        }
    },
    [keepLatestCentered, normalizedChartType, timeframe],
  );

  const onViewportChange = useCallback(() => {
    if (!drawingEnabled) {
      return;
    }
    if (viewportFrameRef.current !== null) {
      return;
    }
    viewportFrameRef.current = requestAnimationFrame(() => {
      viewportFrameRef.current = null;
      setViewportVersion((previous) => previous + 1);
    });
  }, [drawingEnabled]);

  const activeTradePriceLines = useMemo(() => {
    const isLiveMode = timeframe === "Tick" && normalizedChartType === "area";
    if (!isLiveMode) return [];

    const now = Date.now() / 1000 + serverTimeOffsetRef.current;
    const lines = [];
    const seen = new Set();

    // Collect from activeOrders
    if (Array.isArray(activeOrders)) {
      for (const order of activeOrders) {
        if (order.status && order.status !== "active" && order.status !== "settling") continue;
        const price = Number(order.entryPrice);
        if (!Number.isFinite(price) || price <= 0) continue;
        const key = String(order.id || price);
        if (seen.has(key)) continue;
        seen.add(key);

        const isUp = order.side === "up";
        lines.push({
          id: key,
          price,
          color: isUp ? LIVE_PRICE_UP_COLOR : LIVE_PRICE_DOWN_COLOR,
          lineWidth: 1,
          lineStyle: "dashed",
          axisLabelVisible: false,
          title: "",
        });
      }
    }

    // Also collect from orderTimeMarkers
    if (Array.isArray(orderTimeMarkers)) {
      for (const marker of orderTimeMarkers) {
        const expiry = Number(marker.expiryTime);
        if (Number.isFinite(expiry) && now >= expiry + 0.35) continue;
        const price = Number(marker.entryPrice);
        if (!Number.isFinite(price) || price <= 0) continue;
        const key = String(marker.id || price);
        if (seen.has(key)) continue;
        seen.add(key);

        const isUp = marker.side === "up";
        lines.push({
          id: key,
          price,
          color: isUp ? LIVE_PRICE_UP_COLOR : LIVE_PRICE_DOWN_COLOR,
          lineWidth: 1,
          lineStyle: "dashed",
          axisLabelVisible: false,
          title: "",
        });
      }
    }

    return lines;
  }, [activeOrders, orderTimeMarkers, timeframe, normalizedChartType]);

  const effectiveReferencePriceLines = useMemo(() => {
    if (!Array.isArray(referencePriceLines) || referencePriceLines.length === 0) {
      return activeTradePriceLines;
    }
    return [...referencePriceLines, ...activeTradePriceLines];
  }, [referencePriceLines, activeTradePriceLines]);

  const {
    pushPrice,
    getCoordinates,
    getTimeCoordinate,
    getPriceTimeFromCoordinates,
    getPlottingBounds,
    zoomIn,
    zoomOut,
    resetZoom,
  } = useRealtimeAreaChart({
    containerRef: chartContainerRef,
    timeframe,
    precision,
    tickSize,
    chartType: normalizedChartType,
    lineData,
    candleData,
    startingPrice: feedStartPriceSeed,
    onPriceChange: onChartPriceChange,
    onLiveMarkerChange,
    onViewportChange,
    disableInteractions,
    hideTimeScale,
    disableZoomToLatest,
    fixedZoomPercent,
    keepLatestCentered,
    centeredVisibleBars,
    forwardSpaceBars,
    smoothShiftDurationMs,
    chartBackgroundColor,
    chartFontSize,
    priceScaleStep,
    priceScaleLevels,
    pinPriceScale,
    showLivePriceLine,
    referencePriceLines: effectiveReferencePriceLines,
    marketSymbol,
    chartProfile,
    activeOrders,
  });
  // Expose latest getTimeCoordinate to the per-frame onLiveMarkerChange DOM update.
  getTimeCoordinateRef.current = getTimeCoordinate;
    const getPlottingBoundsRef = useRef(getPlottingBounds);
  getPlottingBoundsRef.current = getPlottingBounds;

  // ── Continuous 60fps / 120fps Smooth Shrink Loop for Order Countdown Markers ──
  useEffect(() => {
    const isLiveMode = timeframe === "Tick" && normalizedChartType === "area";
    if (!showOrderTimeMarkers || !isLiveMode || orderTimeMarkers.length === 0) {
      return;
    }

    let rafId = null;

    const animateMarkers = () => {
      const layer = expiryMarkerLayerRef.current;
      const getBounds = getPlottingBoundsRef.current;
      const bounds = typeof getBounds === "function" ? getBounds() : null;

      if (layer && bounds && Number.isFinite(bounds.startBound) && Number.isFinite(bounds.endBound)) {
        const now = Date.now() / 1000 + serverTimeOffsetRef.current;
        const markers = orderTimeMarkersRef.current;
        const expiryEls = layer.querySelectorAll(".order-time-segment");
        const { startBound, endBound } = bounds;

        expiryEls.forEach((el) => {
          const eid = el.dataset.markerId;
          const marker = markers.find((m) => String(m.id) === eid);
          if (!marker) return;

          const expiryTime = Number(marker.expiryTime);
          const entryTime = Number(marker.entryTime);
          if (!Number.isFinite(expiryTime) || !Number.isFinite(entryTime)) return;

          if (now >= expiryTime + 0.35) {
            el.style.opacity = "0";
            return;
          }

          const totalDuration = Math.max(0.05, expiryTime - entryTime);
          const elapsed = Math.max(0, now - entryTime);
          const progress = Math.min(1, Math.max(0, elapsed / totalDuration));

          const currentLeft = startBound + (endBound - startBound) * progress;
          const currentWidth = Math.max(0, endBound - currentLeft);

          el.style.left = `${currentLeft}px`;
          el.style.width = `${currentWidth}px`;
          el.style.opacity = "1";
          el.dataset.near = now >= expiryTime - 2 ? "true" : "false";
        });
      }

      rafId = requestAnimationFrame(animateMarkers);
    };

    rafId = requestAnimationFrame(animateMarkers);
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [showOrderTimeMarkers, timeframe, normalizedChartType, orderTimeMarkers.length > 0]);

  const visibleOrderTimeMarkers = useMemo(() => {
    const isLiveMode = timeframe === "Tick" && normalizedChartType === "area";
    const container = chartContainerRef.current;
    if (!isLiveMode || !container || orderTimeMarkers.length === 0) {
      return [];
    }

    const bounds = getPlottingBounds?.() || (() => {
      const cw = container.clientWidth || 800;
      const col = cw / 6;
      return { startBound: col + 30, endBound: Math.min(col * 5, cw - 66) };
    })();

    const { startBound, endBound } = bounds;
    const nextMarkers = [];
    const now = Date.now() / 1000 + serverTimeOffsetRef.current;
    let index = 0;

    for (const marker of orderTimeMarkers) {
      if (now >= Number(marker.expiryTime) + 0.35) continue;

      const totalDuration = Math.max(0.05, Number(marker.expiryTime) - Number(marker.entryTime));
      const elapsed = Math.max(0, now - Number(marker.entryTime));
      const progress = Math.min(1, Math.max(0, elapsed / totalDuration));

      const currentLeft = startBound + (endBound - startBound) * progress;

      nextMarkers.push({
        id: marker.id,
        tone: marker.side,
        startX: currentLeft,
        endX: endBound,
        expiryTime: marker.expiryTime,
        entryTime: marker.entryTime,
        index: index++,
      });
    }
    return nextMarkers;
  }, [getPlottingBounds, normalizedChartType, orderTimeMarkers, timeframe, viewportVersion]);
  const queueGameAnnotation = useCallback(
    (orderId, label, tone, kind, price, time, ttlSeconds = 8) => {
      const expiresAt = Date.now() / 1000 + ttlSeconds;
      setGameAnnotations((previous) => {
        const next = [
          ...previous,
          {
            id: orderId,
            label,
            tone,
            kind,
            price,
            time,
            expiresAt,
          },
        ];
        return next.slice(-18);
      });
    },
    [],
  );

  const settlePendingOrders = useCallback(
    (currentPrice, now) => {
      if (pendingOrdersRef.current.length === 0) {
        return;
      }
      const wallClockNow = Date.now() / 1000;
      const wallClockMs = Date.now();
      const stillPending = [];
      for (const order of pendingOrdersRef.current) {
        // Contract duration is strictly real-time elapsed duration
        const isExpired = order.expiresAtMs
          ? wallClockMs >= order.expiresAtMs
          : (timeframe === "Tick" ? (now >= order.expiryTime || wallClockNow >= order.expiryTime) : wallClockNow >= order.expiryTime);

        if (!isExpired) {
          stillPending.push(order);
          continue;
        }
        // Contract duration completed on chart. Notify panel that duration expired.
        // NOTE: Win/loss calculation is strictly authoritative on the backend.
        // Client must NEVER speculate or trigger false win popups.
        if (typeof onOrderSettled === "function") {
          try {
            onOrderSettled({
              id: order.id,
              side: order.side,
              amount: order.amount,
              durationSeconds: order.durationSeconds,
              entryPrice: order.entryPrice,
              entryTime: order.entryTime,
              expiryTime: order.expiryTime,
              exitPrice: currentPrice,
              settledAt: now || wallClockNow,
              isExpired: true,
            });
          } catch (_error) {
            // Keep settlement stable even if callback throws.
          }
        }
      }
      pendingOrdersRef.current = stillPending;
    },
    [onOrderSettled, timeframe],
  );
  const trackTickHistory = useCallback((price, time, sourceTick) => {
    const history = initialHistoryRef.current;
    const lastPoint = history[history.length - 1];
    const activeTimeframe = timeframeRef.current;
    const stepDelta = activeTimeframe === "Tick" ? 0.3 : 1;
    const normalizedTime =
      !lastPoint || time >= lastPoint.time
        ? time
        : Math.round((lastPoint.time + stepDelta) * 10) / 10;
    if (lastPoint && lastPoint.time === normalizedTime) {
      lastPoint.value = price;
      applyTickCandleData(lastPoint, sourceTick, price);
    } else {
      const nextPoint = {
        time: normalizedTime,
        value: price,
      };
      applyTickCandleData(nextPoint, sourceTick, price);
      history.push(nextPoint);
    }
    if (history.length > MAX_STORED_TICK_HISTORY_POINTS) {
      history.splice(0, history.length - MAX_STORED_TICK_HISTORY_POINTS);
    }

    if (activeTimeframe === "Tick" && globalTickHistoryRef.current) {
      const gHistory = globalTickHistoryRef.current;
      const gLast = gHistory[gHistory.length - 1];
      const gNormTime =
        !gLast || time >= gLast.time
          ? time
          : Math.round((gLast.time + stepDelta) * 10) / 10;
      if (gLast && gLast.time === gNormTime) {
        gLast.value = price;
        applyTickCandleData(gLast, sourceTick, price);
      } else {
        const nextGPoint = { time: gNormTime, value: price };
        applyTickCandleData(nextGPoint, sourceTick, price);
        gHistory.push(nextGPoint);
      }
      if (gHistory.length > MAX_STORED_TICK_HISTORY_POINTS) {
        gHistory.splice(0, gHistory.length - MAX_STORED_TICK_HISTORY_POINTS);
      }
    }
  }, []);
  const stopAnimation = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);
  useEffect(() => {
    tickMotionRef.current = {
      noise: 0,
      velocity: 0,
      spike: 0,
      phase: 0,
      displayPrice: null,
      spikeCooldownFrames: 0,
    };
  }, [marketSymbol, timeframe]);
  useEffect(() => {
    pendingOrdersRef.current = [];
    setGameAnnotations([]);
    setOrderTimeMarkers([]);
    lastOrderMarkerPositionRef.current = null;
    simulationRef.current = {
      active: false,
      targetPrice: null,
      velocity: 0,
      tradeId: null,
    };

    if (tradeResultTimerRef.current !== null) {
      clearTimeout(tradeResultTimerRef.current);
      tradeResultTimerRef.current = null;
    }
    if (tradeResultOverlayRef.current) {
      tradeResultOverlayRef.current.style.display = "none";
      tradeResultOverlayRef.current.innerHTML = "";
    }
  }, [marketSymbol]);
  useEffect(() => {
    const overlayElement = directionalOverlayRef.current;
    const baseAlpha = resolveOverlayBaseAlpha();
    overlayStateRef.current = {
      green: baseAlpha.green,
      red: baseAlpha.red,
      baseGreen: baseAlpha.green,
      baseRed: baseAlpha.red,
      lastPrice: normalizedStartingPrice,
    };
    if (!overlayElement) {
      return;
    }
    overlayElement.dataset.direction = "up";
    overlayElement.style.setProperty(
      "--overlay-green-alpha",
      baseAlpha.green.toFixed(3),
    );
    overlayElement.style.setProperty(
      "--overlay-red-alpha",
      baseAlpha.red.toFixed(3),
    );
  }, [
    directionalOverlay,
    marketSymbol,
    normalizedStartingPrice,
    resolveOverlayBaseAlpha,
    timeframe,
  ]);
  const animateToTarget = useCallback(() => {
    const frame = (frameTime) => {
      const state = animationStateRef.current;
      if (!state) {
        rafIdRef.current = null;
        return;
      }
      const elapsed = frameTime - state.startAt;
      const progress = Math.min(elapsed / state.durationMs, 1);
      const eased = smoothContinuousMotion
        ? easeInOutCubic(progress)
        : 1 - Math.pow(1 - progress, 3);
      const nextPrice = state.from + (state.to - state.from) * eased;
      const displayedPrice =
        timeframe === "Tick" && !accurateLivePrice
          ? interpolateTickDisplayPrice(
            nextPrice,
            state.to,
            tickMotionRef.current,
          )
          : nextPrice;
      latestTickTimeRef.current = state.targetTime;
      const safePrice = Number.isFinite(displayedPrice)
        ? displayedPrice
        : state.to;
      lastRenderedPriceRef.current = safePrice;
      trackTickHistory(safePrice, state.targetTime, state.sourceTick);
      pushPrice(safePrice, state.targetTime, state.sourceTick);
      settlePendingOrders(safePrice, state.targetTime);
      updateLiveBadge(safePrice, state.direction);
      if (progress >= 1) {
        animationStateRef.current = null;
        rafIdRef.current = null;
        return;
      }
      rafIdRef.current = requestAnimationFrame(frame);
    };
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(frame);
    }
  }, [
    accurateLivePrice,
    pushPrice,
    settlePendingOrders,
    smoothContinuousMotion,
    timeframe,
    trackTickHistory,
    updateLiveBadge,
  ]);
  const enqueueFeedTick = useCallback(
    (tick) => {
      queuedFeedTickRef.current = tick;
      if (feedRafRef.current !== null) {
        return;
      }
      const flush = (now) => {
        const queuedTick = queuedFeedTickRef.current;
        if (!queuedTick) {
          feedRafRef.current = null;
          return;
        }
        if (now - lastFeedFrameAtRef.current < FEED_FRAME_MIN_MS) {
          feedRafRef.current = requestAnimationFrame(flush);
          return;
        }
        lastFeedFrameAtRef.current = now;
        queuedFeedTickRef.current = null;
        const activeTimeframe = timeframeRef.current;
        const stepDelta = activeTimeframe === "Tick" ? 0.3 : 1;
        const normalizedTickTime = Number.isFinite(queuedTick.time) && queuedTick.time > latestTickTimeRef.current
          ? Math.round(queuedTick.time * 10) / 10
          : Math.round((latestTickTimeRef.current + stepDelta) * 10) / 10;
        const targetTick = { ...queuedTick, time: normalizedTickTime };

        if (accurateLivePrice && !smoothContinuousMotion) {
          latestTickTimeRef.current = targetTick.time;
          lastRenderedPriceRef.current = targetTick.price;
          trackTickHistory(targetTick.price, targetTick.time, targetTick);
          pushPrice(targetTick.price, targetTick.time, targetTick);
          settlePendingOrders(targetTick.price, targetTick.time);
          updateLiveBadge(targetTick.price, targetTick.direction);
          if (queuedFeedTickRef.current) {
            feedRafRef.current = requestAnimationFrame(flush);
            return;
          }
          feedRafRef.current = null;
          return;
        }
        animationStateRef.current = {
          from: lastRenderedPriceRef.current,
          to: targetTick.price,
          startAt: now,
          durationMs: smoothContinuousMotion
            ? resolveSmoothJumpDurationMs(
              lastRenderedPriceRef.current,
              targetTick.price,
              resolveFeedAnimationDurationMs(
                latestTickTimeRef.current,
                targetTick.time,
              ),
            )
            : resolveFeedAnimationDurationMs(
              latestTickTimeRef.current,
              targetTick.time,
            ),
          direction: targetTick.direction,
          targetTime: targetTick.time,
          sourceTick: targetTick,
        };
        animateToTarget();
        if (queuedFeedTickRef.current) {
          feedRafRef.current = requestAnimationFrame(flush);
          return;
        }
        feedRafRef.current = null;
      };
      feedRafRef.current = requestAnimationFrame(flush);
    },
    [
      accurateLivePrice,
      animateToTarget,
      pushPrice,
      settlePendingOrders,
      smoothContinuousMotion,
      trackTickHistory,
      updateLiveBadge,
    ],
  );

  const placeOrder = useCallback(
    (request) => {
      const requestedEntryPrice = Number(request?.entryPrice);
      const requestedEntryTime = Number(request?.entryTime);
      const requestedExpiryTime = Number(request?.expiryTime);
      const requestedDuration = Number(request?.durationSeconds);
      const durationSeconds =
        Number.isFinite(requestedDuration) && requestedDuration > 0
          ? requestedDuration
          : 10;
      const entryPrice =
        Number.isFinite(requestedEntryPrice) && requestedEntryPrice > 0
          ? requestedEntryPrice
          : lastRenderedPriceRef.current;
      const nowMs = Date.now();
      const nowSec = nowMs / 1000;
      const expiresAtMs = nowMs + durationSeconds * 1000;
      const entryTime =
        timeframe === "Tick"
          ? (Number.isFinite(requestedEntryTime) && requestedEntryTime > 0
            ? requestedEntryTime
            : (Number.isFinite(latestTickTimeRef.current) && latestTickTimeRef.current > 0
              ? latestTickTimeRef.current
              : nowSec))
          : nowSec;
      const expiryTime =
        timeframe === "Tick"
          ? (Number.isFinite(requestedExpiryTime) && requestedExpiryTime > entryTime
            ? requestedExpiryTime
            : entryTime + durationSeconds)
          : (expiresAtMs / 1000);
      const rawOrderId = String(request?.orderId || "").trim();
      const orderId =
        rawOrderId ||
        `order-${entryTime}-${Math.random().toString(16).slice(2, 8)}`;
      const tradeMult = Number(request.tradeMultiplier || request.jackpot_multiplier || request.appliedMultiplier || 1);
      const effectiveMult = tradeMult > 1 ? tradeMult : 1;
      const order = {
        id: orderId,
        side: request.side,
        amount: request.amount,
        durationSeconds,
        entryPrice,
        entryTime,
        expiryTime,
        createdAtMs: nowMs,
        expiresAtMs,
        payoutMultiplier: request.payoutMultiplier,
        tradeMultiplier: effectiveMult,
        forceLoss: request.forceLoss || false,
      };
      const details = {
        id: orderId,
        side: request.side,
        amount: request.amount,
        durationSeconds,
        payoutMultiplier: request.payoutMultiplier,
        tradeMultiplier: effectiveMult,
        entryPrice,
        entryTime,
        expiryTime,
        createdAtMs: nowMs,
        expiresAtMs,
        potentialProfit: request.amount * (request.payoutMultiplier - 1) * effectiveMult,
        potentialPayout: request.amount * (1 + (request.payoutMultiplier - 1) * effectiveMult),
      };
      pendingOrdersRef.current.push(order);

      // Safety timeout ensures order always settles at exact contract duration even if price ticks are delayed
      setTimeout(() => {
        if (pendingOrdersRef.current.some((o) => o.id === orderId)) {
          settlePendingOrders(lastRenderedPriceRef.current, Date.now() / 1000);
        }
      }, durationSeconds * 1000 + 50);

      lastOrderMarkerPositionRef.current = {
        x: resolveTimeMarkerCoordinate(entryTime, timeframe, getTimeCoordinate),
        y: 18,
      };
      setOrderTimeMarkers((previous) => {
        const nextMarkers = [
          ...previous,
          {
            id: orderId,
            side: request.side,
            entryPrice,
            amount: request.amount,
            payoutMultiplier: request.payoutMultiplier,
            tradeMultiplier: effectiveMult,
            entryTime,
            expiryTime,
            expiresAt: expiryTime + 0.35,
          },
        ];
        return nextMarkers.slice(-16);
      });

      return details;
    },
    [getTimeCoordinate, timeframe, settlePendingOrders],
  );

  const removeOrder = useCallback((orderId) => {
    const normalizedOrderId = String(orderId || "").trim();
    if (!normalizedOrderId) {
      return false;
    }
    // Track this ID so no hydration path can re-add the marker
    removedOrderIdsRef.current.add(normalizedOrderId);
    // Auto-clear after 60s to prevent unbounded growth
    setTimeout(() => removedOrderIdsRef.current.delete(normalizedOrderId), 60000);
    const initialLength = pendingOrdersRef.current.length;
    pendingOrdersRef.current = pendingOrdersRef.current.filter(
      (order) => order.id !== normalizedOrderId,
    );
    setOrderTimeMarkers((previous) =>
      previous.filter((marker) => marker.id !== normalizedOrderId),
    );
    setGameAnnotations((previous) =>
      previous.filter(
        (annotation) =>
          !String(annotation?.id || "").includes(normalizedOrderId),
      ),
    );
    return pendingOrdersRef.current.length !== initialLength;
  }, []);

  const pushExternalTick = useCallback(
    (payload) => {
      const nextPrice = Number(payload?.price);
      if (!Number.isFinite(nextPrice) || nextPrice <= 0) {
        return;
      }
      const receivedAtMs = Date.now();
      const rawTs = Number(payload?.ts ?? payload?.time);
      const tickTime = Number.isFinite(rawTs)
        ? Math.trunc(rawTs >= 1_000_000_000_000 ? rawTs / 1000 : rawTs)
        : Math.max(
          latestTickTimeRef.current + 1,
          Math.floor(Date.now() / 1000),
        );

      const localTime = Date.now() / 1000;
      const diff = tickTime - localTime;
      if (Math.abs(diff - serverTimeOffsetRef.current) > 1) {
        serverTimeOffsetRef.current = diff;
      }

      const previousPrice = lastRenderedPriceRef.current;

      const now = Math.floor(Date.now() / 1000 + serverTimeOffsetRef.current);

      const activeTrade = pendingOrdersRef.current
        .filter((order) => order.expiryTime >= now)
        .sort((a, b) => a.expiryTime - b.expiryTime)[0];

      const timeLeft = activeTrade ? activeTrade.expiryTime - now : null;

      if (
        activeTrade &&
        timeLeft <= 5 &&
        timeLeft > 0 &&
        simulationRef.current.tradeId !== activeTrade.id
      ) {
        const isWin = !activeTrade.forceLoss;

        const volatilityFactor = 0.00002 + Math.random() * 0.00008;
        const offset = activeTrade.entryPrice * volatilityFactor;

        let targetPrice;

        if (activeTrade.side === "up") {
          targetPrice = isWin
            ? activeTrade.entryPrice + offset
            : activeTrade.entryPrice - offset;
        } else {
          targetPrice = isWin
            ? activeTrade.entryPrice - offset
            : activeTrade.entryPrice + offset;
        }

        simulationRef.current = {
          active: true,
          targetPrice,
          velocity: 0,
          tradeId: activeTrade.id,
        };
      }

      let finalPrice = nextPrice;

      if (
        activeTrade &&
        simulationRef.current.active &&
        timeLeft <= 5 &&
        timeLeft > 0
      ) {
        const currentPrice = lastRenderedPriceRef.current;
        const target = simulationRef.current.targetPrice;

        const progress = (5 - timeLeft) / 5;
        const distance = target - currentPrice;

        const bias = distance * 0.15 * progress;

        const volatility = currentPrice * 0.0002;
        const noise = (Math.random() - 0.5) * volatility;

        let velocity = simulationRef.current.velocity;
        velocity += bias + noise;
        velocity *= 0.85;

        let simulatedPrice = currentPrice + velocity;

        // strong convergence near expiry
        if (timeLeft < 0.3) {
          simulatedPrice += (target - simulatedPrice) * 0.6;
        }

        simulationRef.current.velocity = velocity;

        finalPrice = simulatedPrice;
      }

      if (
        activeTrade &&
        activeTrade.expiryTime <= now &&
        simulationRef.current.active
      ) {
        finalPrice = simulationRef.current.targetPrice;
        simulationRef.current.active = false;
        simulationRef.current.tradeId = null;
      } else if (!activeTrade && simulationRef.current.active) {
        // Unlikely fallback if time skips past exactly
        finalPrice = simulationRef.current.targetPrice;
        simulationRef.current.active = false;
        simulationRef.current.tradeId = null;
      }

      if (!activeTrade && !simulationRef.current.active) {
        simulationRef.current.tradeId = null;
      }

      let safeFinal = Number.isFinite(finalPrice) ? finalPrice : nextPrice;

      // ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â°ÃƒÆ’Ã¢â‚¬Â¦Ãƒâ€šÃ‚Â¸ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚ÂºÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¡ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¯ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¸ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â Sanity Check: Prevent extreme values that crash the chart library (Lightweight Charts limit)
      if (Math.abs(safeFinal) > 90071992547409) {
        safeFinal = previousPrice;
      }
      if (
        !isMeaningfulPriceChange(previousPrice, safeFinal, tickSize) &&
        receivedAtMs - lastMeaningfulPriceChangeAtRef.current >=
        IDLE_MICRO_MOVE_AFTER_MS
      ) {
        safeFinal = resolveIdleMicroPrice(
          previousPrice,
          idleMicroMotionRef.current,
          tickSize,
        );
      }
      if (isMeaningfulPriceChange(previousPrice, safeFinal, tickSize)) {
        lastMeaningfulPriceChangeAtRef.current = receivedAtMs;
      }

      enqueueFeedTick({
        price: safeFinal,
        time: tickTime,
        direction: safeFinal >= previousPrice ? "up" : "down",
        open: previousPrice,
        high: Math.max(previousPrice, safeFinal),
        low: Math.min(previousPrice, safeFinal),
        close: safeFinal,
        sourceTimeMs: Number.isFinite(rawTs) ? rawTs : Date.now(),
      });
    },
    [enqueueFeedTick, tickSize],
  );

  useEffect(() => {
    if (!activeOrders || !Array.isArray(activeOrders)) return;

    const normalize = (s) =>
      String(s || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
    const toSeconds = (val) => {
      if (!val) return 0;
      if (typeof val === "number")
        return val > 1e12 ? val / 1000 : val;
      const d = new Date(val);
      const ms = d.getTime();
      return isNaN(ms) ? 0 : ms / 1000;
    };

    const currentMarket = normalize(marketSymbol);
    // Only consider orders that are truly active (not settled/closed/cancelled)
    const ACTIVE_STATUSES = new Set(["active", "open", "pending", ""]);
    const symbolOrders = activeOrders.filter((o) => {
      if (normalize(o.pair || o.symbol) !== currentMarket) return false;
      const id = String(o.id || "");
      // Skip orders that were explicitly removed (early-closed)
      if (removedOrderIdsRef.current.has(id)) return false;
      // Skip orders that have a non-active status (settled, won, lost, closed, cancelled)
      const status = String(o.status || "").toLowerCase().trim();
      if (status && !ACTIVE_STATUSES.has(status)) return false;
      return true;
    });

    // Prune markers whose orders are no longer active for this symbol
    const activeOrderIds = new Set(symbolOrders.map((o) => String(o.id || "")));
    setOrderTimeMarkers((prev) => {
      const pruned = prev.filter((m) => {
        const mid = String(m.id || "");
        // Keep markers that don't belong to this market (they're managed by another chart instance)
        // But remove markers that DO belong to this market and are no longer in activeOrders
        if (removedOrderIdsRef.current.has(mid)) return false;
        return true;
      });
      if (pruned.length !== prev.length) return pruned;
      return prev;
    });

    if (symbolOrders.length === 0) return;

    symbolOrders.forEach((o) => {
      const orderId = String(o.id || "");
      if (!orderId) return;
      const rawMult = Number(o.payoutMultiplier ?? o.profitPercent ?? o.multiplier ?? 0);
      const normalizedMult = rawMult > 10 ? 1 + rawMult / 100 : (rawMult > 0 ? rawMult : 1.82);
      if (!pendingOrdersRef.current.some((po) => String(po.id || "") === orderId)) {
        pendingOrdersRef.current.push({
          id: orderId,
          side: o.side,
          amount: Number(o.amount || 0),
          durationSeconds: Number(o.durationSeconds || 0),
          entryPrice: Number(o.entryPrice || 0),
          entryTime: toSeconds(o.entryTime || o.startTime),
          expiryTime: toSeconds(o.expiryTime || o.endTime),
          payoutMultiplier: normalizedMult,
          forceLoss: o.forceLoss || false,
        });
      }
    });

    setOrderTimeMarkers((prev) => {
      let changed = false;
      const next = [...prev];
      symbolOrders.forEach((o) => {
        const orderId = String(o.id || "");
        if (!orderId) return;
        const entryTime = toSeconds(o.entryTime || o.startTime);
        const expiryTime = toSeconds(o.expiryTime || o.endTime);
        if (entryTime === 0 || expiryTime === 0) return;
        const rawMult = Number(o.payoutMultiplier ?? o.profitPercent ?? o.multiplier ?? 0);
        const normalizedMult = rawMult > 10 ? 1 + rawMult / 100 : (rawMult > 0 ? rawMult : 1.82);
        const tradeMult = Number(
          o.tradeMultiplier ??
          (o.isLuckyRound && Number(o.jackpot_multiplier) > 1 ? o.jackpot_multiplier : (o.appliedMultiplier || o.jackpot_multiplier || 1))
        );
        const effectiveMult = tradeMult > 1 ? tradeMult : 1;

        const existingIdx = next.findIndex((m) => String(m.id || "") === orderId);
        if (existingIdx >= 0) {
          const cur = next[existingIdx];
          if (!cur.entryPrice || !cur.amount || !cur.payoutMultiplier || cur.tradeMultiplier !== effectiveMult || cur.entryTime !== entryTime || cur.expiryTime !== expiryTime) {
            next[existingIdx] = {
              ...cur,
              entryPrice: Number(o.entryPrice || cur.entryPrice || 0),
              amount: Number(o.amount || cur.amount || 0),
              payoutMultiplier: normalizedMult,
              tradeMultiplier: effectiveMult,
              entryTime,
              expiryTime,
              expiresAt: expiryTime + 0.35,
            };
            changed = true;
          }
        } else {
          next.push({
            id: orderId,
            side: o.side,
            amount: Number(o.amount || 0),
            entryPrice: Number(o.entryPrice || 0),
            payoutMultiplier: normalizedMult,
            tradeMultiplier: effectiveMult,
            entryTime,
            expiryTime,
            expiresAt: expiryTime + 0.35,
          });
          changed = true;
        }
      });
      return changed ? next.slice(-16) : prev;
    });
  }, [
    activeOrders,
    marketSymbol,
    timeframe,
    isHistoricalReady,
    getTimeCoordinate,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      placeOrder,
      removeOrder,
      getCurrentPrice: () => lastRenderedPriceRef.current,
      getCurrentTickTime: () => {
        const nowSec = Date.now() / 1000;
        return Number.isFinite(latestTickTimeRef.current) && latestTickTimeRef.current > 0
          ? (timeframe === "Tick" ? latestTickTimeRef.current : nowSec)
          : nowSec;
      },
      getLiveMarkerPercentX: () => liveMarkerXPercentRef.current,
      isChartLoading: () => isChartLoading,
      pushExternalTick,
      zoomIn: (factor) => zoomIn?.(factor),
      zoomOut: (factor) => zoomOut?.(factor),
      resetZoom: () => resetZoom?.(),
      showTradeResult: (result) => {
        if (tradeResultTimerRef.current !== null) {
          clearTimeout(tradeResultTimerRef.current);
          tradeResultTimerRef.current = null;
        }

        const overlay = tradeResultOverlayRef.current;
        if (!overlay) return;

        const anchor = liveMarkerPositionRef.current;

        if (!anchor && liveDotRef.current) {
          const dotRect = liveDotRef.current.getBoundingClientRect();
          const stageRect =
            chartContainerRef.current.parentElement.getBoundingClientRect();

          anchor = {
            x: dotRect.left - stageRect.left + dotRect.width / 2,
            y: dotRect.top - stageRect.top + dotRect.height / 2,
          };
        }
        const container = chartContainerRef.current;
        const stageRect = container?.parentElement?.getBoundingClientRect();
        const fallbackX = stageRect
          ? Math.max(12, Math.round(stageRect.width * 0.68))
          : 12;
        const fallbackY = stageRect
          ? Math.max(16, Math.round(stageRect.height * 0.82))
          : 16;
        const position = anchor ?? { x: fallbackX, y: fallbackY };

        const normalizedResultStatus = String(result?.status || "")
          .trim()
          .toLowerCase();
        const resultIsWin = normalizedResultStatus
          ? ["won", "win", "winner", "success"].includes(normalizedResultStatus)
          : Number(result?.profit) >= 0;
        const resultAmount = Number.isFinite(Number(result?.profit))
          ? Math.abs(Number(result?.profit))
          : 0;
        const formattedResultAmount =
          resultAmount > 0 &&
            resultAmount < 1 &&
            Number(resultAmount.toFixed(3)) !== Number(resultAmount.toFixed(2))
            ? resultAmount.toFixed(3)
            : resultAmount.toFixed(2);

        overlay.innerHTML = `
          <span
            class="trade-result-marker"
            data-tone="${resultIsWin ? "up" : "down"}"
            style="left: ${position.x}px; top: ${position.y}px;"
          >
            ${resultIsWin ? "+" : "-"}${formattedResultAmount}
          </span>
        `;
        overlay.style.display = "block";

        tradeResultTimerRef.current = setTimeout(() => {
          overlay.style.display = "none";
          overlay.innerHTML = "";
          tradeResultTimerRef.current = null;
        }, 4000);
      },
    }),
    [placeOrder, pushExternalTick, removeOrder, isChartLoading, timeframe, zoomIn, zoomOut, resetZoom],
  );

  useEffect(() => {
    const requestId = historicalRequestIdRef.current + 1;
    historicalRequestIdRef.current = requestId;
    setIsChartLoading(true);
    historicalReadyRef.current = false;
    setIsHistoricalReady(false);
    queuedFeedTickRef.current = null;
    if (feedMode === "randomWalk") {
      const seededHistory = generateInitialHistory(
        normalizedStartingPrice,
        INITIAL_TICK_HISTORY_POINTS,
      );
      initialHistoryRef.current = seededHistory;
      const seedPoint = seededHistory[seededHistory.length - 1];
      if (seedPoint) {
        lastRenderedPriceRef.current = seedPoint.value;
        latestTickTimeRef.current = seedPoint.time;
        setFeedStartPriceSeed(seedPoint.value);
      }
      setHistoryVersion((previous) => previous + 1);
      historicalReadyRef.current = true;
      setIsHistoricalReady(true);
      setIsChartLoading(false);
      return () => { };
    }
    setHistoryVersion((previous) => previous + 1);
    const controller = new AbortController();
    const loadHistoricalData = async () => {
      try {
        const historicalConfig = resolveHistoricalFetchConfig(timeframe);
        const historicalTicks = await fetchHistoricalTickData({
          symbol: marketSymbol,
          points: historicalConfig.points,
          interval: historicalConfig.interval,
          signal: controller.signal,
        });
        if (historicalRequestIdRef.current !== requestId) {
          return;
        }
        const fallbackPrice = Number.isFinite(lastRenderedPriceRef.current) && lastRenderedPriceRef.current > 0
          ? lastRenderedPriceRef.current
          : normalizedStartingPrice;
        const history =
          historicalTicks.length > 0
            ? historicalTicks
            : [
              {
                time: Math.floor(Date.now() / 1000),
                value: fallbackPrice,
                open: fallbackPrice,
                high: fallbackPrice,
                low: fallbackPrice,
                close: fallbackPrice,
              },
            ];
        if (history.length === 0) {
          return;
        }
        const processedHistory =
          timeframe === "Tick" && historicalTicks.length > 0
            ? expand1sToTickHistory(history)
            : history;
        initialHistoryRef.current = processedHistory;
        if (timeframe === "Tick") {
          globalTickHistoryRef.current = [...processedHistory];
        }
        const latestPoint = processedHistory[processedHistory.length - 1];
        if (latestPoint) {
          latestTickTimeRef.current = latestPoint.time;
          lastRenderedPriceRef.current = latestPoint.value;
          setFeedStartPriceSeed(latestPoint.value);
        }
        setHistoryVersion((previous) => previous + 1);
      } catch (_error) {
        if (historicalRequestIdRef.current !== requestId) {
          return;
        }
        const fallbackPrice = Number.isFinite(lastRenderedPriceRef.current) && lastRenderedPriceRef.current > 0
          ? lastRenderedPriceRef.current
          : normalizedStartingPrice;
        const fallbackHistory = generateInitialHistory(
          fallbackPrice,
          INITIAL_TICK_HISTORY_POINTS,
        );
        initialHistoryRef.current = fallbackHistory;
        if (timeframe === "Tick" && !globalTickHistoryRef.current) {
          globalTickHistoryRef.current = [...fallbackHistory];
        }
        const latestPoint = fallbackHistory[fallbackHistory.length - 1];
        if (latestPoint) {
          latestTickTimeRef.current = latestPoint.time;
          lastRenderedPriceRef.current = latestPoint.value;
          setFeedStartPriceSeed(latestPoint.value);
        }
        setHistoryVersion((previous) => previous + 1);
      } finally {
        if (historicalRequestIdRef.current === requestId) {
          historicalReadyRef.current = true;
          setIsHistoricalReady(true);
          setIsChartLoading(false);
        }
      }
    };
    loadHistoricalData();
    return () => {
      controller.abort();
    };
  }, [
    feedMode,
    historySeedKey,
    marketSymbol,
    timeframe,
  ]);

  useEffect(() => {
    if (
      feedMode !== "randomWalk" &&
      (!isHistoricalReady || !historicalReadyRef.current)
    ) {
      return () => { };
    }
    const fallbackSeedPrice =
      initialHistoryRef.current[initialHistoryRef.current.length - 1]?.value ??
      normalizedStartingPrice;
    const feedStartPrice = Number.isFinite(feedStartPriceSeed)
      ? feedStartPriceSeed
      : fallbackSeedPrice;

    if (feedMode === "external") {
      return () => {
        stopAnimation();
        if (feedRafRef.current !== null) {
          cancelAnimationFrame(feedRafRef.current);
          feedRafRef.current = null;
        }
        queuedFeedTickRef.current = null;
      };
    }

    if (feedMode === "randomWalk") {
      const stepMagnitude = Number.isFinite(randomWalkStep)
        ? Math.max(0.01, randomWalkStep)
        : 3;
      const intervalMs = Number.isFinite(randomWalkIntervalMs)
        ? Math.max(150, Math.round(randomWalkIntervalMs))
        : 500;
      let previousPrice = Number.isFinite(feedStartPrice)
        ? feedStartPrice
        : normalizedStartingPrice;
      let timerId = null;

      const pushRandomTick = () => {
        const delta = (Math.random() * 2 - 1) * stepMagnitude;
        const nextPrice = Math.max(MIN_DISPLAY_PRICE, previousPrice + delta);
        const stepDelta = timeframe === "Tick" ? 0.3 : 1;
        const tickTime = Math.max(
          Math.round((latestTickTimeRef.current + stepDelta) * 10) / 10,
          Math.round((Date.now() / 1000) * 10) / 10
        );
        const nextTick = {
          price: nextPrice,
          time: tickTime,
          direction: nextPrice >= previousPrice ? "up" : "down",
          open: previousPrice,
          high: Math.max(previousPrice, nextPrice),
          low: Math.min(previousPrice, nextPrice),
          close: nextPrice,
          sourceTimeMs: Date.now(),
        };
        previousPrice = nextPrice;
        enqueueFeedTick(nextTick);
      };

      timerId = setInterval(pushRandomTick, intervalMs);
      return () => {
        stopAnimation();
        if (feedRafRef.current !== null) {
          cancelAnimationFrame(feedRafRef.current);
          feedRafRef.current = null;
        }
        queuedFeedTickRef.current = null;
        if (timerId !== null) {
          clearInterval(timerId);
        }
      };
    }

    const feed = new BinanceTradeFeed({
      symbol: marketSymbol,
      startPrice: feedStartPrice,
      heartbeatMs: forceTickEveryMs,
      enableSyntheticFallback,
    });
    const unsubscribe = feed.subscribe(
      ({ price, time, direction, open, high, low, close, sourceTimeMs }) => {
        const receivedAtMs = Date.now();
        const previousPrice = Number.isFinite(lastRenderedPriceRef.current)
          ? lastRenderedPriceRef.current
          : feedStartPrice;
        let displayPrice = Number.isFinite(price) ? price : previousPrice;
        if (
          !isMeaningfulPriceChange(previousPrice, displayPrice, tickSize) &&
          receivedAtMs - lastMeaningfulPriceChangeAtRef.current >=
          IDLE_MICRO_MOVE_AFTER_MS
        ) {
          displayPrice = resolveIdleMicroPrice(
            previousPrice,
            idleMicroMotionRef.current,
            tickSize,
          );
        }
        if (isMeaningfulPriceChange(previousPrice, displayPrice, tickSize)) {
          lastMeaningfulPriceChangeAtRef.current = receivedAtMs;
        }
        const nextTick = {
          price: displayPrice,
          time,
          direction:
            displayPrice >= previousPrice ? "up" : (direction ?? "down"),
          open: previousPrice,
          high: Math.max(
            Number.isFinite(high) ? high : previousPrice,
            previousPrice,
            displayPrice,
          ),
          low: Math.min(
            Number.isFinite(low) ? low : previousPrice,
            previousPrice,
            displayPrice,
          ),
          close: displayPrice,
          sourceTimeMs,
        };
        enqueueFeedTick(nextTick);
      },
    );
    feed.start();
    return () => {
      stopAnimation();
      if (feedRafRef.current !== null) {
        cancelAnimationFrame(feedRafRef.current);
        feedRafRef.current = null;
      }
      queuedFeedTickRef.current = null;
      unsubscribe();
      feed.stop();
    };
  }, [
    enableSyntheticFallback,
    enqueueFeedTick,
    feedMode,
    feedStartPriceSeed,
    forceTickEveryMs,
    isHistoricalReady,
    marketSymbol,
    randomWalkIntervalMs,
    randomWalkStep,
    stopAnimation,
    tickSize,
  ]);

  useEffect(() => {
    if (
      feedMode === "randomWalk" ||
      !isHistoricalReady ||
      !historicalReadyRef.current
    ) {
      return () => { };
    }
    lastMeaningfulPriceChangeAtRef.current = Date.now();
    idleMicroMotionRef.current = {
      phase: 0,
      velocity: 0,
    };
    const pushIdleMicroMove = () => {
      const nowMs = Date.now();
      if (
        nowMs - lastMeaningfulPriceChangeAtRef.current <
        IDLE_MICRO_MOVE_AFTER_MS
      ) {
        return;
      }
      if (animationStateRef.current || queuedFeedTickRef.current) {
        return;
      }
      const previousPrice = Number.isFinite(lastRenderedPriceRef.current)
        ? lastRenderedPriceRef.current
        : normalizedStartingPrice;
      const nextPrice = resolveIdleMicroPrice(
        previousPrice,
        idleMicroMotionRef.current,
        tickSize,
      );
      if (
        !Number.isFinite(nextPrice) ||
        nextPrice <= 0 ||
        nextPrice === previousPrice
      ) {
        return;
      }
      const stepDelta = timeframe === "Tick" ? 0.3 : 1;
      const tickTime = Math.max(
        Math.round((latestTickTimeRef.current + stepDelta) * 10) / 10,
        Math.round((nowMs / 1000) * 10) / 10
      );
      lastMeaningfulPriceChangeAtRef.current = nowMs;
      enqueueFeedTick({
        price: nextPrice,
        time: tickTime,
        direction: nextPrice >= previousPrice ? "up" : "down",
        open: previousPrice,
        high: Math.max(previousPrice, nextPrice),
        low: Math.min(previousPrice, nextPrice),
        close: nextPrice,
        sourceTimeMs: nowMs,
        syntheticIdle: true,
      });
    };
    idleMicroTimerRef.current = setInterval(
      pushIdleMicroMove,
      IDLE_MICRO_MOVE_INTERVAL_MS,
    );
    return () => {
      if (idleMicroTimerRef.current !== null) {
        clearInterval(idleMicroTimerRef.current);
        idleMicroTimerRef.current = null;
      }
    };
  }, [
    enqueueFeedTick,
    feedMode,
    isHistoricalReady,
    normalizedStartingPrice,
    tickSize,
  ]);

  useEffect(() => {
    if (gameAnnotations.length === 0) {
      return;
    }
    const timerId = setInterval(() => {
      const now = Date.now() / 1000;
      setGameAnnotations((previous) =>
        previous.filter(
          (annotation) => (annotation.expiresAt ?? now + 1) > now,
        ),
      );
    }, 200);
    return () => {
      clearInterval(timerId);
    };
  }, [gameAnnotations.length]);

  useEffect(() => {
    if (orderTimeMarkers.length === 0) {
      return;
    }
    const timerId = setInterval(() => {
      const now = Date.now() / 1000;
      setOrderTimeMarkers((previous) =>
        previous.filter((marker) => (marker.expiresAt ?? now + 1) > now),
      );
    }, 200);
    return () => {
      clearInterval(timerId);
    };
  }, [orderTimeMarkers.length]);

  useEffect(() => {
    if (!isHistoricalReady) {
      return;
    }
    setIsChartLoading(true);
    if (timeframeLoaderTimerRef.current !== null) {
      clearTimeout(timeframeLoaderTimerRef.current);
    }
    timeframeLoaderTimerRef.current = setTimeout(() => {
      setIsChartLoading(false);
      timeframeLoaderTimerRef.current = null;
    }, INTERVAL_CHANGE_LOADER_MS);
    return () => {
      if (timeframeLoaderTimerRef.current !== null) {
        clearTimeout(timeframeLoaderTimerRef.current);
        timeframeLoaderTimerRef.current = null;
      }
    };
  }, [isHistoricalReady, timeframe]);

  useEffect(() => {
    return () => {
      if (viewportFrameRef.current !== null) {
        cancelAnimationFrame(viewportFrameRef.current);
        viewportFrameRef.current = null;
      }
      if (feedRafRef.current !== null) {
        cancelAnimationFrame(feedRafRef.current);
        feedRafRef.current = null;
      }
      if (timeframeLoaderTimerRef.current !== null) {
        clearTimeout(timeframeLoaderTimerRef.current);
        timeframeLoaderTimerRef.current = null;
      }
      if (tradeResultTimerRef.current !== null) {
        clearTimeout(tradeResultTimerRef.current);
        tradeResultTimerRef.current = null;
      }
    };
  }, []);

  const hasResolvedStartPrice = feedMode === "randomWalk" || isHistoricalReady;
  const headerSeedPrice =
    hasResolvedStartPrice && Number.isFinite(feedStartPriceSeed)
      ? feedStartPriceSeed
      : null;
  return (
    <article className={cn("trading-card", cardClassName)}>
      <header className="trading-card__header">
        <p ref={symbolBadgeRef} className="symbol-badge" data-direction="up">
          {symbol}
        </p>
        <span ref={livePriceLabelRef} className="live-price">
          {headerSeedPrice === null
            ? "--"
            : formatPriceToSevenDigits(headerSeedPrice)}
        </span>
      </header>
      <div className={cn("chart-stage", stageClassName)}>
        <div
          ref={chartContainerRef}
          className={cn("chart-canvas", canvasClassName)}
        />

        {directionalOverlay ? (
          <div
            ref={directionalOverlayRef}
            className={cn("chart-direction-overlay", overlayClassName)}
            aria-hidden="true"
          />
        ) : null}
        {isChartLoading && !hideChartLoader ? (
          <div
            className="chart-loader-overlay"
            role="status"
            aria-live="polite"
          >
            <span className="chart-loader-spinner" aria-hidden="true" />
            <p>Loading market data...</p>
          </div>
        ) : null}
        <div
          ref={tradeResultOverlayRef}
          className="trade-result-layer"
          style={{ display: "none", pointerEvents: "none" }}
          aria-hidden="true"
        />
        {/* Order countdown & duration time markers on the top time bar - Live Mode only */}
        {showOrderTimeMarkers && timeframe === "Tick" && normalizedChartType === "area" ? (
          <OrderTimeMarkersLayer
            ref={expiryMarkerLayerRef}
            visibleOrderTimeMarkers={visibleOrderTimeMarkers}
          />
        ) : null}

        {/* Active Live Trades Layer - Live Mode line graph only */}
        {showOrderTimeMarkers && timeframe === "Tick" && normalizedChartType === "area" ? (
          <ActiveTradesLayer
            ref={activeTradeLayerRef}
            orderTimeMarkers={orderTimeMarkers}
          />
        ) : null}

        {timeframe === "Tick" && normalizedChartType === "area" && gameAnnotations.length > 0 ? (
          <div className="annotation-layer" aria-hidden="true">
            <AnnotationLayer
              containerRef={chartContainerRef}
              annotations={gameAnnotations}
              getCoordinates={getCoordinates}
              viewportVersion={viewportVersion}
              timeframe={timeframe}
            />
          </div>
        ) : null}
        <DrawingLayer
          enabled={drawingEnabled}
          tool={drawingTool}
          resetKey={drawingsResetKey}
          viewportVersion={viewportVersion}
          getCoordinates={getCoordinates}
          getPriceTimeFromCoordinates={getPriceTimeFromCoordinates}
          timeframe={timeframe}
        />
        {showLiveMarker ? (
          <LiveMarkerLayer
            showLivePriceLine={showLivePriceLine}
            normalizedChartType={normalizedChartType}
            timeframe={timeframe}
            headerSeedPrice={headerSeedPrice}
            liveLevelRef={liveLevelRef}
            liveDotRef={liveDotRef}
            livePillRef={livePillRef}
          />
        ) : null}
      </div>
    </article>
  );
});

TradingChartComponent.displayName = "TradingChart";

export const TradingChart = memo(
  TradingChartComponent,
  areTradingChartPropsEqual,
);

export default TradingChart;
